import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Badge,
  Button,
  SpinnerLoader,
  Toastify,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Label,
  Textarea,
} from "lift-ui";
import { ChevronRight, ChevronDown, Database, Table as TableIcon, Pencil, Check, X } from "lucide-react";
import apiClient from '@lift-admin/utils/apiClient';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';

// --- Types ---

interface Project {
  project_id: string;
  name: string;
}

interface ProjectsResponse {
  projects: Project[];
}

interface DatasetResponse {
  project_id: string;
  datasets: string[];
}

interface TableInfo {
  tableName: string;
  tableType: string;
  id: string;
}

interface TableListResponse {
  project_id: string;
  dataset_name: string;
  tables: TableInfo[];
}

interface ColumnInfo {
  mode: string;
  dataType: string;
  columnName: string;
  description: string | null;
}

interface TableDetailsResponse {
  id: string;
  dataset_id: string;
  table_name: string;
  columns_data: {
    columns: ColumnInfo[];
  };
  created_at: string;
  updated_at: string;
}

// --- Constants ---
const API_BASE_URL = "http://localhost:8000";

// --- Components ---

const BigQueryMetadata: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [datasets, setDatasets] = useState<string[]>([]);
  const [expandedDatasets, setExpandedDatasets] = useState<Record<string, boolean>>({});
  const [tablesByDataset, setTablesByDataset] = useState<Record<string, TableInfo[]>>({});
  const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});
  const [tableDetails, setTableDetails] = useState<Record<string, TableDetailsResponse>>({});
  
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingTables, setLoadingTables] = useState<Record<string, boolean>>({});
  const [loadingTableDetails, setLoadingTableDetails] = useState<Record<string, boolean>>({});

  // New state for editing
  const [editingColumn, setEditingColumn] = useState<{
    tableId: string;
    datasetName: string;
    tableName: string;
    columnName: string;
    description: string;
  } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);

  const handleEditClick = (tableId: string, datasetName: string, tableName: string, columnName: string, currentDescription: string | null) => {
    setEditingColumn({
      tableId,
      datasetName,
      tableName,
      columnName,
      description: currentDescription || ""
    });
    setSheetOpen(true);
  };

  const handleSheetClose = () => {
    setSheetOpen(false);
    // Delay clearing the editing column to avoid UI flicker during closing animation
    setTimeout(() => setEditingColumn(null), 300);
  };

  const handleSheetSave = async () => {
    if (!editingColumn) return;
    setSavingDescription(true);
    // Capture the previous description BEFORE local state is updated, for the change log.
    const previousDescription =
      tableDetails[editingColumn.tableId]?.columns_data.columns.find(
        col => col.columnName === editingColumn.columnName
      )?.description ?? '';
    try {
      await apiClient.post(`/lift-report-builder/api/v1/bigquery/update-column-descriptions`, {
         project_id: selectedProject,
         dataset_name: editingColumn.datasetName,
         table_name: editingColumn.tableName,
         columns: [
           {
             columnName: editingColumn.columnName,
             description: editingColumn.description
           }
         ]
      });

      // Update local state
      setTableDetails(prev => {
        const table = prev[editingColumn.tableId];
        if (!table) return prev;
        const updatedColumns = table.columns_data.columns.map(col =>
          col.columnName === editingColumn.columnName ? { ...col, description: editingColumn.description } : col
        );
        return {
          ...prev,
          [editingColumn.tableId]: {
            ...table,
            columns_data: {
              ...table.columns_data,
              columns: updatedColumns
            }
          }
        };
      });

      Toastify("success", "Description updated successfully");
      const clip = (s: string) => (s && s.length > 160 ? `${s.slice(0, 160)}…` : s);
      const changes =
        previousDescription !== (editingColumn.description ?? '')
          ? [
              {
                label: 'Description',
                from: clip(previousDescription) || '(empty)',
                to: clip(editingColumn.description ?? '') || '(empty)',
              },
            ]
          : [];
      await logAdminAction(
        `BigQuery column description updated: ${editingColumn.tableName}.${editingColumn.columnName}`,
        {
          url: `/lift-report-builder/api/v1/bigquery/update-column-descriptions`,
          method: 'POST',
          status: 200,
          category: 'settings',
          action: 'update_bigquery_column_description',
          project: selectedProject,
          dataset: editingColumn.datasetName,
          table: editingColumn.tableName,
          column: editingColumn.columnName,
          changes,
        }
      );
      handleSheetClose();
    } catch (error: any) {
      console.error("Error updating description:", error);
      Toastify("error", "Failed to update description");
      await logAdminAction(
        `Failed to update BigQuery column description (${editingColumn.tableName}.${editingColumn.columnName}): ${error?.response?.data?.detail || error?.message || 'Unknown error'}`,
        { url: `/lift-report-builder/api/v1/bigquery/update-column-descriptions`, method: 'POST', category: 'settings', action: 'update_bigquery_column_description' },
        true
      );
    } finally {
      setSavingDescription(false);
    }
  };

  // Fetch Projects
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await apiClient.get<ProjectsResponse>(`/lift-report-builder/api/v1/bigquery/metadata/projects`);
        setProjects(response.data.projects);
        if (response.data.projects.length > 0) {
          setSelectedProject(response.data.projects[0].project_id);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        Toastify("error", "Failed to fetch projects");
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch Datasets when Project Changes
  useEffect(() => {
    if (!selectedProject) return;

    const fetchDatasets = async () => {
      setLoadingDatasets(true);
      setDatasets([]);
      setExpandedDatasets({});
      setTablesByDataset({});
      setExpandedTables({});
      setTableDetails({});

      try {
        const response = await apiClient.get<DatasetResponse>(`/lift-report-builder/api/v1/bigquery/metadata/datasets`, {
          params: { project_id: selectedProject },
        });
        setDatasets(response.data.datasets);
      } catch (error) {
        console.error("Error fetching datasets:", error);
        Toastify("error", "Failed to fetch datasets");
      } finally {
        setLoadingDatasets(false);
      }
    };

    fetchDatasets();
  }, [selectedProject]);

  const toggleDataset = useCallback(async (datasetName: string) => {
    setExpandedDatasets((prev) => {
      const isExpanded = !prev[datasetName];
      
      if (isExpanded && !tablesByDataset[datasetName]) {
        // Fetch tables if expanding and not already loaded
        setLoadingTables((prevLoading) => ({ ...prevLoading, [datasetName]: true }));
        apiClient.get<TableListResponse>(`/lift-report-builder/api/v1/bigquery/metadata/tables`, {
          params: { project_id: selectedProject, dataset_name: datasetName },
        })
        .then((response) => {
          setTablesByDataset((prevTables) => ({ ...prevTables, [datasetName]: response.data.tables }));
        })
        .catch((error) => {
          console.error(`Error fetching tables for ${datasetName}:`, error);
          Toastify("error", `Failed to fetch tables for ${datasetName}`);
        })
        .finally(() => {
          setLoadingTables((prevLoading) => ({ ...prevLoading, [datasetName]: false }));
        });
      }

      return { ...prev, [datasetName]: isExpanded };
    });
  }, [selectedProject, tablesByDataset]);

  const toggleTable = useCallback(async (tableId: string) => {
    setExpandedTables((prev) => {
      const isExpanded = !prev[tableId];

      if (isExpanded && !tableDetails[tableId]) {
        // Fetch table details if expanding and not already loaded
        setLoadingTableDetails((prevLoading) => ({ ...prevLoading, [tableId]: true }));
        apiClient.get<TableDetailsResponse>(`/lift-report-builder/api/v1/bigquery/metadata/table/${tableId}`)
        .then((response) => {
          setTableDetails((prevDetails) => ({ ...prevDetails, [tableId]: response.data }));
        })
        .catch((error) => {
          console.error(`Error fetching details for table ${tableId}:`, error);
          Toastify("error", "Failed to fetch table details");
        })
        .finally(() => {
          setLoadingTableDetails((prevLoading) => ({ ...prevLoading, [tableId]: false }));
        });
      }

      return { ...prev, [tableId]: isExpanded };
    });
  }, [tableDetails]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      <PageHeader title="BigQuery Metadata" />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-8 pt-4 pb-4">
        {loadingProjects ? (
          <div className="flex items-center justify-center h-full">
            <SpinnerLoader />
          </div>
        ) : (
          <Tabs value={selectedProject} onValueChange={setSelectedProject} className="w-full flex-1 flex flex-col min-h-0">
            <TabsList className="w-full justify-start overflow-x-auto mb-4">
              {projects.map((project) => (
                <TabsTrigger key={project.project_id} value={project.project_id} className="px-6">
                  {project.name}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={selectedProject} className="mt-0 flex-1 flex flex-col min-h-0">
              <div className="flex-1 flex flex-col bg-white min-h-0 rounded-lg shadow-sm overflow-hidden border">
                {loadingDatasets ? (
                  <div className="flex items-center justify-center py-20">
                    <SpinnerLoader />
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-4">
                    {datasets.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">No datasets found for this project.</div>
                    ) : (
                      <div className="space-y-2">
                        {datasets.map((datasetName) => (
                          <div key={datasetName} className="border rounded-md overflow-hidden">
                            <div
                              className="flex items-center p-3 bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => toggleDataset(datasetName)}
                            >
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 mr-2">
                                {expandedDatasets[datasetName] ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                              <Database className="h-4 w-4 mr-2 text-primary" />
                              <span className="font-medium">{datasetName}</span>
                            </div>

                            {expandedDatasets[datasetName] && (
                              <div className="border-t bg-background p-2 pl-8">
                                {loadingTables[datasetName] ? (
                                  <div className="flex justify-center py-4">
                                    <SpinnerLoader className="h-4 w-4" />
                                  </div>
                                ) : !tablesByDataset[datasetName] || tablesByDataset[datasetName].length === 0 ? (
                                  <div className="text-sm text-muted-foreground py-2 pl-2">No tables found.</div>
                                ) : (
                                  <div className="space-y-1">
                                    {tablesByDataset[datasetName].map((table) => (
                                      <div key={table.id} className="border rounded overflow-hidden">
                                        <div
                                          className="flex items-center p-2 hover:bg-muted/30 cursor-pointer text-sm"
                                          onClick={() => toggleTable(table.id)}
                                        >
                                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 mr-2">
                                            {expandedTables[table.id] ? (
                                              <ChevronDown className="h-3 w-3" />
                                            ) : (
                                              <ChevronRight className="h-3 w-3" />
                                            )}
                                          </Button>
                                          <TableIcon className="h-3 w-3 mr-2 text-muted-foreground" />
                                          <span className="font-medium">{table.tableName}</span>
                                          <Badge variant="outline" className="ml-auto text-xs h-5">
                                            {table.tableType}
                                          </Badge>
                                        </div>

                                        {expandedTables[table.id] && (
                                          <div className="border-t bg-muted/5 p-4">
                                            {loadingTableDetails[table.id] ? (
                                              <div className="flex justify-center py-2">
                                                <SpinnerLoader className="h-4 w-4" />
                                              </div>
                                            ) : tableDetails[table.id] ? (
                                              <div className="rounded-md border bg-background overflow-hidden">
                                                <Table>
                                                  <TableHeader>
                                                    <TableRow className="h-8">
                                                      <TableHead className="h-8 text-xs font-semibold">Column Name</TableHead>
                                                      <TableHead className="h-8 text-xs font-semibold">Data Type</TableHead>
                                                      <TableHead className="h-8 text-xs font-semibold">Mode</TableHead>
                                                      <TableHead className="h-8 text-xs font-semibold">Description</TableHead>
                                                    </TableRow>
                                                  </TableHeader>
                                                  <TableBody>
                                                    {tableDetails[table.id].columns_data.columns.map((col, idx) => (
                                                      <TableRow key={idx} className="h-8 hover:bg-transparent group/row">
                                                        <TableCell className="py-1 text-xs font-medium">{col.columnName}</TableCell>
                                                        <TableCell className="py-1 text-xs">
                                                          <Badge variant="secondary" className="font-mono text-[10px] h-5">
                                                            {col.dataType}
                                                          </Badge>
                                                        </TableCell>
                                                        <TableCell className="py-1 text-xs text-muted-foreground">{col.mode}</TableCell>
                                                      <TableCell className="py-1 text-xs text-muted-foreground relative">
                                                        <div className="flex items-center justify-between group-hover/row:bg-muted/50 rounded px-1 -ml-1 pr-2">
                                                          <span className="truncate max-w-[300px]" title={col.description || ""}>{col.description || "-"}</span>
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-5 w-5 p-0 opacity-0 group-hover/row:opacity-100 transition-opacity"
                                                            onClick={() => handleEditClick(table.id, datasetName, tableDetails[table.id].table_name, col.columnName, col.description)}
                                                          >
                                                            <Pencil className="h-3 w-3 text-muted-foreground" />
                                                          </Button>
                                                        </div>
                                                      </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </div>
                                            ) : (
                                              <div className="text-xs text-destructive">Failed to load details.</div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex flex-col h-full w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Edit Column Description</SheetTitle>
            <SheetDescription>
              Update the description for column <strong>{editingColumn?.columnName}</strong> in table <strong>{editingColumn?.tableName}</strong>.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 py-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter column description..."
                  value={editingColumn?.description || ""}
                  onChange={(e) => setEditingColumn(prev => prev ? { ...prev, description: e.target.value } : null)}
                  rows={8}
                  className="resize-none"
                />
              </div>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={handleSheetClose} disabled={savingDescription}>
              Cancel
            </Button>
            <Button onClick={handleSheetSave} disabled={savingDescription}>
              {savingDescription ? <SpinnerLoader className="mr-2 h-4 w-4" /> : null}
              Save Changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BigQueryMetadata;
